import type { ComponentProps } from "react";
import { EventIcon, type EventIconName } from "@/components/icons";
import { cn } from "@/lib/utils";

export type EventTagVariant = EventIconName | string;

type EventTagProps = {
  variant: EventTagVariant;
  /** Faerbt den Badge nach dem Zugang statt nach dem Typ. */
  memberOnly?: boolean;
  inactive?: boolean;
} & ComponentProps<"span">;

/**
 * Die Farbe steht fuer den **Zugang**, nicht fuer den Typ.
 *
 * Frueher hatte jeder Typ seine eigene: DiaTalk sand, TopicTalk sky-75,
 * Sprechstunde sky-100, Community sun-100, Reisen sun-50. Fuenf Farben, die
 * nichts darueber sagten, ob der Nutzer hineinkommt.
 *
 * Jetzt: sand fuer kostenlos, sun fuer Member-Only. Der Mitgliedschaftsvorteil
 * ist damit auf jeder Karte sichtbar, statt nur im Schloss-Symbol.
 */
const KOSTENLOS_BG = "bg-sand-100";
const MEMBER_ONLY_BG = "bg-sun-100";

const variantConfig: Record<EventIconName, { label: string }> = {
  diaTalk: { label: "DiaTalk" },
  topicTalk: { label: "TopicTalks" },
  sprechstunde: { label: "Sprechstunde" },
  community: { label: "Community" },
  reisen: { label: "Reisen" },
  // Info-Termin zu einer Reise — eigenes Etikett, gleiche Optik.
  reiseInfo: { label: "Reise-Info" },
};

const isKnownVariant = (variant: string): variant is EventIconName => {
  return variant in variantConfig;
};

const EventTag = ({
  variant,
  memberOnly = false,
  inactive = false,
  className,
  ...rest
}: EventTagProps) => {
  const isKnown = isKnownVariant(variant);
  const config = isKnown ? variantConfig[variant] : { label: variant };

  return (
    <span
      className={cn(
        "text-tag inline-flex items-center gap-1 rounded-full px-2 py-1 text-black-80",
        inactive
          ? "bg-white opacity-50"
          : memberOnly
            ? MEMBER_ONLY_BG
            : KOSTENLOS_BG,
        className,
      )}
      {...rest}
    >
      {isKnown && (
        <EventIcon
          name={variant}
          className="size-4"
        />
      )}
      {config.label}
    </span>
  );
};

export { EventTag };
